import { Button, LearnMoreLink } from '@auctisync/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { HouseSelector } from '../../components/HouseSelector';
import { useToast } from '../../components/Toast';
import { useOwnedTenants } from '../../hooks/useOwnedTenants';
import { supabase } from '../../lib/supabase';

/**
 * Tenant admin — Invoice Settings (owner-only).
 *
 * Each auction house configures how its invoices read and how bidders pay:
 * who checks are payable to + remit-to address, which payment methods are
 * accepted, pickup defaults, terms & conditions, and invoice appearance.
 * Everything is stored in tenants.metadata.invoice_settings (a jsonb sub-key)
 * per the shared invoice data contract.
 *
 * Reads via get_invoice_settings() and writes via set_invoice_settings()
 * (owner-only, SECURITY DEFINER RPCs) — never a direct tenants.update(), since
 * owners may not hold an RLS UPDATE grant on public.tenants. The reader returns
 * the RAW stored jsonb; this page is the SINGLE place that materializes field
 * defaults, so the on-the-wire contract stays minimal.
 *
 * Multi-house owners (review finding #9): both RPCs take p_tenant_id, resolved
 * here from useOwnedTenants + a HouseSelector (only rendered when the caller
 * owns more than one house). Every read AND write is pinned to the selected
 * house — no more "first owner row wins".
 *
 * Customer-facing text defaults are intentionally BLANK — placeholders give
 * guidance, but we never invent legal T&C copy on a tenant's behalf.
 */

// ── Shared data contract shape (tenants.metadata.invoice_settings) ──────────
interface AddressFields {
  line1: string;
  line2: string;
  city: string;
  region: string;
  postal_code: string;
  country: string;
}

interface PickupDefault {
  location: string;
  address: AddressFields;
  instructions: string;
  dates_text: string;
}

interface AcceptedMethods {
  card_online: boolean;
  cash: boolean;
  check: boolean;
}

interface InvoiceSettings {
  make_checks_payable_to: string;
  remit_to: AddressFields;
  check_instructions: string;
  pickup_default: PickupDefault;
  terms_and_conditions: string;
  invoice_footer: string;
  thank_you_note: string;
  show_tax_column: boolean;
  accepted_methods: AcceptedMethods;
  offline_note: string;
}

// ── Field length caps (chars). Text is trimmed to these before saving. ──────
const CAP_SHORT = 200; // single-line names/lines
const CAP_MEDIUM = 500; // notes / instructions
const CAP_LONG = 8000; // terms & conditions

const EMPTY_ADDRESS: AddressFields = {
  line1: '',
  line2: '',
  city: '',
  region: '',
  postal_code: '',
  country: '',
};

const DEFAULT_SETTINGS: InvoiceSettings = {
  make_checks_payable_to: '',
  remit_to: { ...EMPTY_ADDRESS },
  check_instructions: '',
  pickup_default: {
    location: '',
    address: { ...EMPTY_ADDRESS },
    instructions: '',
    dates_text: '',
  },
  terms_and_conditions: '',
  invoice_footer: '',
  thank_you_note: '',
  show_tax_column: true,
  accepted_methods: { card_online: true, cash: false, check: false },
  offline_note: '',
};

/**
 * Typed bridge for the two invoice-settings RPCs. `database.ts` is regenerated
 * centrally after this migration is applied; until then these names aren't in
 * the generated union, so we call through a narrow, fully-typed signature (no
 * `any`) rather than widening the shared client. Remove once db:types runs.
 */
type InvoiceRpc = {
  (
    fn: 'get_invoice_settings',
    args: { p_tenant_id: string | null },
  ): Promise<{ data: unknown; error: { message: string } | null }>;
  (
    fn: 'set_invoice_settings',
    args: { p_settings: InvoiceSettings; p_tenant_id: string | null },
  ): Promise<{ data: unknown; error: { message: string } | null }>;
};
const invoiceRpc = supabase.rpc.bind(supabase) as unknown as InvoiceRpc;

/** Coerce an arbitrary jsonb blob into a fully-defaulted, well-typed settings object. */
function normalize(raw: unknown): InvoiceSettings {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const str = (v: unknown): string => (typeof v === 'string' ? v : '');
  const bool = (v: unknown, dflt: boolean): boolean => (typeof v === 'boolean' ? v : dflt);
  const addr = (v: unknown): AddressFields => {
    const a = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
    return {
      line1: str(a['line1']),
      line2: str(a['line2']),
      city: str(a['city']),
      region: str(a['region']),
      postal_code: str(a['postal_code']),
      country: str(a['country']),
    };
  };
  const pickup = (
    o['pickup_default'] && typeof o['pickup_default'] === 'object' ? o['pickup_default'] : {}
  ) as Record<string, unknown>;
  const methods = (
    o['accepted_methods'] && typeof o['accepted_methods'] === 'object' ? o['accepted_methods'] : {}
  ) as Record<string, unknown>;

  return {
    make_checks_payable_to: str(o['make_checks_payable_to']),
    remit_to: addr(o['remit_to']),
    check_instructions: str(o['check_instructions']),
    pickup_default: {
      location: str(pickup['location']),
      address: addr(pickup['address']),
      instructions: str(pickup['instructions']),
      dates_text: str(pickup['dates_text']),
    },
    terms_and_conditions: str(o['terms_and_conditions']),
    invoice_footer: str(o['invoice_footer']),
    thank_you_note: str(o['thank_you_note']),
    show_tax_column: bool(o['show_tax_column'], true),
    accepted_methods: {
      card_online: bool(methods['card_online'], true),
      cash: bool(methods['cash'], false),
      check: bool(methods['check'], false),
    },
    offline_note: str(o['offline_note']),
  };
}

/** Trim every text field to its cap right before persisting. */
function capForSave(s: InvoiceSettings): InvoiceSettings {
  const capAddr = (a: AddressFields): AddressFields => ({
    line1: a.line1.slice(0, CAP_SHORT),
    line2: a.line2.slice(0, CAP_SHORT),
    city: a.city.slice(0, CAP_SHORT),
    region: a.region.slice(0, CAP_SHORT),
    postal_code: a.postal_code.slice(0, CAP_SHORT),
    country: a.country.slice(0, CAP_SHORT),
  });
  return {
    make_checks_payable_to: s.make_checks_payable_to.slice(0, CAP_SHORT),
    remit_to: capAddr(s.remit_to),
    check_instructions: s.check_instructions.slice(0, CAP_MEDIUM),
    pickup_default: {
      location: s.pickup_default.location.slice(0, CAP_SHORT),
      address: capAddr(s.pickup_default.address),
      instructions: s.pickup_default.instructions.slice(0, CAP_MEDIUM),
      dates_text: s.pickup_default.dates_text.slice(0, CAP_MEDIUM),
    },
    terms_and_conditions: s.terms_and_conditions.slice(0, CAP_LONG),
    invoice_footer: s.invoice_footer.slice(0, CAP_MEDIUM),
    thank_you_note: s.thank_you_note.slice(0, CAP_MEDIUM),
    show_tax_column: s.show_tax_column,
    accepted_methods: { ...s.accepted_methods },
    offline_note: s.offline_note.slice(0, CAP_MEDIUM),
  };
}

export function AdminInvoiceSettings() {
  const toast = useToast();
  const owned = useOwnedTenants();
  const [settings, setSettings] = useState<InvoiceSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pin every RPC call to the selected house. For non-owners selectedId is
  // null and the RPC's own 42501 gate produces the friendly error below.
  const selectedTenantId = owned.selectedId;

  // Latest-wins guard: switching houses in the HouseSelector faster than the
  // RPC resolves must NOT let a stale get_invoice_settings response paint the
  // previous house's settings (which a subsequent save would then write to
  // the newly-selected house). Each load stamps a sequence number; a response
  // that isn't the newest is dropped.
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    const { data, error: err } = await invoiceRpc('get_invoice_settings', {
      p_tenant_id: selectedTenantId,
    });
    if (seq !== loadSeq.current) return; // superseded by a newer house selection
    if (err) {
      setError(
        err.message.includes('42501') || /owner/i.test(err.message)
          ? 'Invoice settings are available to the tenant owner.'
          : err.message,
      );
      setLoading(false);
      return;
    }
    setSettings(normalize(data));
    setLoading(false);
  }, [selectedTenantId]);

  useEffect(() => {
    // Wait for the owned-tenants lookup so a multi-house owner's first read
    // goes to the SELECTED house instead of tripping the RPC's P0001 guard.
    if (owned.loading) return;
    void load();
  }, [load, owned.loading]);

  async function save() {
    setSaving(true);
    setError(null);
    const payload = capForSave(settings);
    const { error: err } = await invoiceRpc('set_invoice_settings', {
      p_settings: payload,
      p_tenant_id: selectedTenantId,
    });
    setSaving(false);
    if (err) {
      setError(err.message);
      toast.error('Invoice settings could not be saved.');
      return;
    }
    setSettings(payload);
    toast.success('Invoice settings saved.');
  }

  // Field updaters (immutable, strongly typed to the nested contract shape).
  function setTop<K extends keyof InvoiceSettings>(key: K, value: InvoiceSettings[K]) {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }
  function setRemit<K extends keyof AddressFields>(key: K, value: string) {
    setSettings((prev) => ({ ...prev, remit_to: { ...prev.remit_to, [key]: value } }));
  }
  function setPickup<K extends keyof Omit<PickupDefault, 'address'>>(key: K, value: string) {
    setSettings((prev) => ({
      ...prev,
      pickup_default: { ...prev.pickup_default, [key]: value },
    }));
  }
  function setPickupAddr<K extends keyof AddressFields>(key: K, value: string) {
    setSettings((prev) => ({
      ...prev,
      pickup_default: {
        ...prev.pickup_default,
        address: { ...prev.pickup_default.address, [key]: value },
      },
    }));
  }
  function setMethod<K extends keyof AcceptedMethods>(key: K, value: boolean) {
    setSettings((prev) => ({
      ...prev,
      accepted_methods: { ...prev.accepted_methods, [key]: value },
    }));
  }

  if (loading) return <div style={wrapStyle}>Loading invoice settings…</div>;

  return (
    <div style={wrapStyle}>
      <header style={{ marginBottom: '1.5rem' }}>
        <h1
          style={{
            fontFamily: 'var(--font-heading-active)',
            fontSize: '1.75rem',
            fontWeight: 700,
            color: 'var(--text)',
            margin: 0,
          }}
        >
          Invoice Settings
        </h1>
        <p style={{ color: 'var(--text-muted)', marginTop: '0.3rem' }}>
          How your invoices read and how buyers pay. These defaults appear on every invoice you
          send.{' '}
          <LearnMoreLink helpSlug="invoice-settings" newTab>
            How invoice settings work
          </LearnMoreLink>
        </p>
      </header>

      <HouseSelector
        tenants={owned.tenants}
        selectedId={owned.selectedId}
        onSelect={owned.setSelectedId}
        disabled={saving}
      />

      {error && (
        <div role="alert" style={errorStyle}>
          {error}
        </div>
      )}

      {/* ── Accepted payment methods ────────────────────────────────── */}
      <section style={cardStyle}>
        <SectionHead
          title="Accepted payment methods"
          description="Which ways buyers can settle an invoice. Turning off card-online hides the pay-now button."
        />
        <CheckRow
          label="Card online"
          hint="pay-now link + saved cards"
          checked={settings.accepted_methods.card_online}
          onChange={(v) => setMethod('card_online', v)}
        />
        <CheckRow
          label="Cash"
          hint="in person, at pickup"
          checked={settings.accepted_methods.cash}
          onChange={(v) => setMethod('cash', v)}
        />
        <CheckRow
          label="Check"
          hint="in person, at pickup"
          checked={settings.accepted_methods.check}
          onChange={(v) => setMethod('check', v)}
        />
        <Field label="Offline payment note" hint="Shown when cash/check is accepted.">
          <textarea
            value={settings.offline_note}
            maxLength={CAP_MEDIUM}
            onChange={(e) => setTop('offline_note', e.target.value)}
            placeholder="e.g. Cash and checks accepted at pickup during posted hours."
            style={textareaStyle}
          />
        </Field>
      </section>

      {/* ── Paying by check ─────────────────────────────────────────── */}
      <section style={cardStyle}>
        <SectionHead
          title="Paying by check"
          description="Where checks are sent and who they're written to. Shown on invoices when Check is accepted."
        />
        <Field label="Make checks payable to">
          <input
            type="text"
            value={settings.make_checks_payable_to}
            maxLength={CAP_SHORT}
            onChange={(e) => setTop('make_checks_payable_to', e.target.value)}
            placeholder="e.g. Downtown Auction Co."
            style={inputStyle}
          />
        </Field>
        <AddressGrid
          legend="Remit-to address"
          value={settings.remit_to}
          onChange={(k, v) => setRemit(k, v)}
        />
        <Field label="Check instructions" hint="Anything else a buyer mailing a check should know.">
          <textarea
            value={settings.check_instructions}
            maxLength={CAP_MEDIUM}
            onChange={(e) => setTop('check_instructions', e.target.value)}
            placeholder="e.g. Write your invoice number in the memo. Allow 5 business days to clear."
            style={textareaStyle}
          />
        </Field>
      </section>

      {/* ── Pickup defaults ─────────────────────────────────────────── */}
      <section style={cardStyle}>
        <SectionHead
          title="Pickup defaults"
          description="Default pickup location, hours, and instructions applied to new auctions."
        />
        <Field label="Location name">
          <input
            type="text"
            value={settings.pickup_default.location}
            maxLength={CAP_SHORT}
            onChange={(e) => setPickup('location', e.target.value)}
            placeholder="e.g. Main Warehouse — Bay 3"
            style={inputStyle}
          />
        </Field>
        <AddressGrid
          legend="Pickup address"
          value={settings.pickup_default.address}
          onChange={(k, v) => setPickupAddr(k, v)}
        />
        <Field label="Pickup dates & hours">
          <textarea
            value={settings.pickup_default.dates_text}
            maxLength={CAP_MEDIUM}
            onChange={(e) => setPickup('dates_text', e.target.value)}
            placeholder="e.g. Tue–Thu after the sale, 9 AM–4 PM. Sat by appointment."
            style={textareaStyle}
          />
        </Field>
        <Field label="Pickup instructions">
          <textarea
            value={settings.pickup_default.instructions}
            maxLength={CAP_MEDIUM}
            onChange={(e) => setPickup('instructions', e.target.value)}
            placeholder="e.g. Bring your paid invoice and a photo ID. Enter through the loading dock."
            style={textareaStyle}
          />
        </Field>
      </section>

      {/* ── Terms & conditions ──────────────────────────────────────── */}
      <section style={cardStyle}>
        <SectionHead
          title="Terms & conditions"
          description="Printed on every invoice. Your own legal terms — we leave this blank for you to fill in."
        />
        <Field label="Terms & conditions text">
          <textarea
            value={settings.terms_and_conditions}
            maxLength={CAP_LONG}
            onChange={(e) => setTop('terms_and_conditions', e.target.value)}
            placeholder="Enter your auction house's terms & conditions. (Consider having counsel review them.)"
            style={{ ...textareaStyle, minHeight: '9rem' }}
          />
        </Field>
      </section>

      {/* ── Invoice appearance ──────────────────────────────────────── */}
      <section style={cardStyle}>
        <SectionHead
          title="Invoice appearance"
          description="Small touches that show on the printed and emailed invoice."
        />
        <CheckRow
          label="Show tax column"
          hint="display a per-line tax column on invoices"
          checked={settings.show_tax_column}
          onChange={(v) => setTop('show_tax_column', v)}
        />
        <Field label="Thank-you note" hint="A short line near the top of the invoice.">
          <input
            type="text"
            value={settings.thank_you_note}
            maxLength={CAP_MEDIUM}
            onChange={(e) => setTop('thank_you_note', e.target.value)}
            placeholder="e.g. Thank you for bidding with us!"
            style={inputStyle}
          />
        </Field>
        <Field label="Invoice footer" hint="Fine print at the very bottom of the invoice.">
          <textarea
            value={settings.invoice_footer}
            maxLength={CAP_MEDIUM}
            onChange={(e) => setTop('invoice_footer', e.target.value)}
            placeholder="e.g. Questions? support@yourhouse.com · (555) 010-0000"
            style={textareaStyle}
          />
        </Field>
      </section>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
        <Button
          onClick={() => {
            void save();
          }}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save settings'}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={saving}
          onClick={() => {
            void load();
          }}
        >
          Revert
        </Button>
      </div>
    </div>
  );
}

// ── Presentational helpers ──────────────────────────────────────────────────

function SectionHead({ title, description }: { title: string; description: string }) {
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>
        {title}
      </h2>
      <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0.15rem 0 0' }}>
        {description}
      </p>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'block', marginBottom: '0.75rem' }}>
      <span
        style={{
          display: 'block',
          fontSize: '0.85rem',
          fontWeight: 500,
          color: 'var(--text)',
          marginBottom: '0.3rem',
        }}
      >
        {label}
        {hint && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> — {hint}</span>}
      </span>
      {children}
    </label>
  );
}

function CheckRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.6rem',
        fontSize: '0.9rem',
        padding: '0.3rem 0',
        cursor: 'pointer',
      }}
    >
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span style={{ color: 'var(--text)' }}>{label}</span>
      {hint && <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>— {hint}</span>}
    </label>
  );
}

function AddressGrid({
  legend,
  value,
  onChange,
}: {
  legend: string;
  value: AddressFields;
  onChange: (key: keyof AddressFields, value: string) => void;
}) {
  return (
    <fieldset style={{ border: 'none', margin: 0, padding: 0, marginBottom: '0.75rem' }}>
      <legend
        style={{
          fontSize: '0.85rem',
          fontWeight: 500,
          color: 'var(--text)',
          marginBottom: '0.4rem',
          padding: 0,
        }}
      >
        {legend}
      </legend>
      <div style={{ display: 'grid', gap: '0.5rem' }}>
        <input
          type="text"
          value={value.line1}
          maxLength={CAP_SHORT}
          onChange={(e) => onChange('line1', e.target.value)}
          placeholder="Address line 1"
          aria-label={`${legend} — line 1`}
          style={inputStyle}
        />
        <input
          type="text"
          value={value.line2}
          maxLength={CAP_SHORT}
          onChange={(e) => onChange('line2', e.target.value)}
          placeholder="Address line 2 (optional)"
          aria-label={`${legend} — line 2`}
          style={inputStyle}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.5rem' }}>
          <input
            type="text"
            value={value.city}
            maxLength={CAP_SHORT}
            onChange={(e) => onChange('city', e.target.value)}
            placeholder="City"
            aria-label={`${legend} — city`}
            style={inputStyle}
          />
          <input
            type="text"
            value={value.region}
            maxLength={CAP_SHORT}
            onChange={(e) => onChange('region', e.target.value)}
            placeholder="State / region"
            aria-label={`${legend} — state or region`}
            style={inputStyle}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
          <input
            type="text"
            value={value.postal_code}
            maxLength={CAP_SHORT}
            onChange={(e) => onChange('postal_code', e.target.value)}
            placeholder="Postal code"
            aria-label={`${legend} — postal code`}
            style={inputStyle}
          />
          <input
            type="text"
            value={value.country}
            maxLength={CAP_SHORT}
            onChange={(e) => onChange('country', e.target.value)}
            placeholder="Country"
            aria-label={`${legend} — country`}
            style={inputStyle}
          />
        </div>
      </div>
    </fieldset>
  );
}

// ── Styles (mirrors AdminAiSettings shell) ──────────────────────────────────

const wrapStyle: React.CSSProperties = {
  padding: '2rem 2.25rem',
  maxWidth: '56rem',
};

const cardStyle: React.CSSProperties = {
  padding: '1rem 1.25rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-card, 0.5rem)',
  background: 'var(--surface-elev)',
  marginBottom: '1rem',
};

const errorStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  border: '1px solid var(--border-danger, #fecaca)',
  marginBottom: '1rem',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '0.45rem 0.6rem',
  fontSize: '0.9rem',
  color: 'var(--text)',
  background: 'var(--surface)',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-tile, 0.375rem)',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  minHeight: '4.5rem',
  resize: 'vertical',
  fontFamily: 'inherit',
};
